import React from "react";
import axios, { AxiosError, AxiosInstance } from "axios";
import { useLocation } from "react-router-dom";
import { request } from "../../api/generated/core/request";
import { ApiClient, CancelablePromise } from "../../api/generated";
import { ApiRequestOptions } from "../../api/generated/core/ApiRequestOptions";
import { AxiosHttpRequest } from "../../api/generated/core/AxiosHttpRequest";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

const AxiosClientContext = React.createContext<ApiClient>(undefined!);

interface ApiProviderProps extends React.PropsWithChildren {
  axiosInstance: AxiosInstance;
}

export default function ApiProvider(props: ApiProviderProps) {
  const location = useLocation();
  const pathname = location.pathname;

  const queryClient = React.useMemo(() => {
    return new QueryClient({
      defaultOptions: {
        queries: {
          retry: (failureCount, error) => {
            if (axios.isAxiosError(error)) {
              const status = (error as AxiosError).response?.status;
              if (status === 404 || status === 403) return false;
            }
            return failureCount < 0;
          },
          retryDelay: 1000 * 60 * 0.15, // 9 / 10 seconds
          refetchOnWindowFocus: false,
          staleTime: 1000 * 60 * 1.5, // 1.5 minutes
        },
        mutations: {
          retry: false,
        },
      },
    });
  }, []);

  const axiosClient = React.useMemo(() => {
    return new ApiClient(
      // X-App is required by the API. The request interceptor also sets it, but
      // declaring it on the client config puts it in the generated request's own
      // header set rather than relying on the interceptor alone.
      { HEADERS: { "X-App": "App" } },
      class AxiosHttpRequestInstance extends AxiosHttpRequest {
        public override request<T>(
          options: ApiRequestOptions
        ): CancelablePromise<T> {
          return request(this.config, options, props.axiosInstance);
        }
      }
    );
  }, []);

  React.useEffect(() => {
    queryClient.invalidateQueries();
    queryClient.resetQueries();
  }, [queryClient, pathname]);

  return (
    <QueryClientProvider client={queryClient}>
      <AxiosInstanceContext.Provider value={props.axiosInstance}>
        <AxiosClientContext.Provider value={axiosClient}>
          {props.children}
        </AxiosClientContext.Provider>
      </AxiosInstanceContext.Provider>
    </QueryClientProvider>
  );
}

export function useAxiosClient() {
  const client = React.useContext(AxiosClientContext);

  if (client == null) {
    throw new Error(
      `Invocations to ${useAxiosClient.name} should be within a node that is a child of ${ApiProvider.name}`
    );
  }

  return client;
}

const AxiosInstanceContext = React.createContext<AxiosInstance>(undefined!);

export function useAxiosInstance() {
  const instance = React.useContext(AxiosInstanceContext);

  if (instance == null) {
    throw new Error(
      `Invocations to ${useAxiosInstance.name} should be within a node that is a child of ${ApiProvider.name}`
    );
  }

  return instance;
}
